import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Zap, X, Coins, Loader2, AlertTriangle } from "lucide-react";

/**
 * Dialog per piazzare (o rimuovere) una scommessa su una sponsorship già attiva.
 * Regola: min 30 crediti, se obiettivo raggiunto → 2x.
 */
export default function SponsorBetDialog({
  leagueId, teamId, sponsorship, otherHasBet = false, currentBudget = 0,
  onClose, onDone,
}) {
  const initial = Number(sponsorship?.bet_amount || 0);
  const [amount, setAmount] = useState(initial > 0 ? initial : 50);
  const [busy, setBusy] = useState(false);

  if (!sponsorship) return null;

  const isEditing = initial > 0;
  const delta = amount - initial; // >0 addebita differenza
  const canAfford = delta <= 0 || currentBudget >= delta;

  const submit = async () => {
    if (amount > 0 && amount < 30) {
      toast.error("Minimo 30 crediti");
      return;
    }
    if (otherHasBet && amount > 0) {
      toast.error("Hai già una scommessa attiva sull'altro sponsor. Puoi scommettere su un solo sponsor.");
      return;
    }
    if (!canAfford) {
      toast.error(`Budget insufficiente: servono ${delta}c aggiuntivi`);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        `/leagues/${leagueId}/teams/${teamId}/sponsorships/${sponsorship.sponsorship_id}/bet`,
        { bet_amount: amount }
      );
      if (amount === 0) toast.success("Scommessa rimossa");
      else toast.success(`⚡ Scommessa di ${amount}c attiva su ${sponsorship.sponsor_name}`);
      onDone?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const remove = () => { setAmount(0); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur"
      data-testid="bet-dialog"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div className="w-full max-w-md bg-slate-900 border border-amber-500/40 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-amber-500/20 via-amber-600/10 to-transparent px-5 py-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="text-amber-400" size={18}/>
            <div>
              <div className="font-black uppercase tracking-[0.2em] text-white text-sm">Scommessa</div>
              <div className="text-[10px] text-slate-400">su {sponsorship.sponsor_name}</div>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-white disabled:opacity-30" data-testid="close-bet">
            <X size={18}/>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {otherHasBet && amount > 0 && (
            <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 p-3 flex items-start gap-2 text-xs text-rose-200">
              <AlertTriangle size={14} className="text-rose-300 shrink-0 mt-0.5"/>
              <div>Hai già una scommessa sull&apos;altro sponsor. Puoi scommettere su un solo sponsor per stagione.</div>
            </div>
          )}

          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-2">
              Crediti da scommettere (min 30)
            </label>
            <div className="flex items-center gap-2">
              <Coins className="text-amber-400" size={16}/>
              <input type="number" min={0} step={5} value={amount}
                onChange={(e) => setAmount(Math.max(0, parseInt(e.target.value) || 0))}
                data-testid="bet-amount-input"
                disabled={busy}
                className="w-28 px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-white text-lg font-mono font-black text-center"/>
              <span className="text-xs text-slate-500">crediti</span>
            </div>
          </div>

          {/* Quick amounts */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Rapidi:</span>
            {[30, 50, 100, 200].map(v => (
              <button key={v} onClick={() => setAmount(v)}
                disabled={busy}
                data-testid={`bet-quick-${v}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  amount === v
                    ? "bg-amber-500 border-amber-400 text-slate-900 font-black"
                    : "bg-slate-800 border-slate-700 text-slate-300 hover:border-amber-500/60"}`}>
                {v}c
              </button>
            ))}
          </div>

          {/* Preview payout */}
          <div className="rounded-xl bg-slate-950/60 border border-slate-800 p-3 space-y-1.5 text-xs">
            <div className="flex items-center justify-between text-slate-400">
              <span>Crediti scalati subito:</span>
              <span className="font-mono text-rose-300">−{amount}c</span>
            </div>
            <div className="flex items-center justify-between text-slate-400">
              <span>Se obiettivo <span className="text-emerald-300 font-bold">centrato</span> → ricevi:</span>
              <span className="font-mono text-emerald-300 font-black">+{amount * 2}c <span className="text-[10px]">(2×)</span></span>
            </div>
            <div className="flex items-center justify-between text-slate-400">
              <span>Se obiettivo <span className="text-rose-300 font-bold">mancato</span>:</span>
              <span className="font-mono text-slate-500">crediti persi</span>
            </div>
            <div className="pt-1.5 mt-1.5 border-t border-slate-800 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Budget dopo:</span>
              <span className={`font-mono font-black ${canAfford ? "text-white" : "text-rose-300"}`}>
                {Math.floor(currentBudget - delta)}c
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-slate-800 bg-slate-950/50">
          {isEditing ? (
            <button onClick={remove} disabled={busy}
              data-testid="bet-clear-btn"
              className="text-xs font-bold text-rose-300 hover:text-rose-200 underline underline-offset-2 disabled:opacity-30">
              Imposta a 0 (rimuovi)
            </button>
          ) : <div/>}
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={busy}
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-300 border border-slate-700 hover:border-slate-500 disabled:opacity-30"
              data-testid="cancel-bet">Annulla</button>
            <button onClick={submit} disabled={busy || (amount > 0 && amount < 30) || !canAfford}
              data-testid="submit-bet"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-[0.2em] bg-gradient-to-br from-amber-400 to-amber-600 text-slate-900 hover:brightness-110 disabled:opacity-30 shadow-lg shadow-amber-500/20">
              {busy ? <><Loader2 className="animate-spin" size={13}/> Salvo…</>
                : amount === 0 ? "Rimuovi"
                : <><Zap size={13}/> {isEditing ? "Aggiorna" : "Conferma"} {amount}c</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
